import { useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import {
  COVERAGE_GAP_CLOSING_REASONS,
  COVERAGE_GAP_NOTE_MAX_LENGTH,
  type CloseCoverageGapBody,
  type CoverageGapClosingReason,
} from "@shared/types/corpus/api.ts";
import { Note } from "../../../../components/primitives/data-display/Note";
import { FormDialog } from "../../../../components/primitives/feedback/FormDialog/FormDialog";
import { useFormDialog } from "../../../../components/primitives/feedback/FormDialog/useFormDialog";
import { COVERAGE_GAP_REASON_LABELS } from "../../../../lib/coverageGapView";

export interface CloseCoverageGapDialogProps {
  open: boolean;
  onClose: () => void;
  gapName: string;
  /** Called with the reason and the note when the person confirms. Throw to surface an error and
   *  keep the dialog open. */
  onSubmit: (body: CloseCoverageGapBody) => Promise<void>;
}

/** Collects why a coverage gap is closed. Closing as filled checks the gap's claims again. */
export function CloseCoverageGapDialog({
  open,
  onClose,
  gapName,
  onSubmit,
}: CloseCoverageGapDialogProps) {
  const [reason, setReason] = useState<CoverageGapClosingReason>("filled");
  const [note, setNote] = useState("");

  const { dialogProps } = useFormDialog({
    onSubmit: async () => {
      await onSubmit({ reason, note: note.trim() });
    },
    onClose: () => {
      setReason("filled");
      setNote("");
      onClose();
    },
  });

  return (
    <FormDialog
      open={open}
      title={`Close “${gapName}”`}
      submitLabel="Close gap"
      maxWidth="sm"
      {...dialogProps}
    >
      <TextField
        select
        label="Reason"
        size="small"
        fullWidth
        value={reason}
        onChange={(e) => setReason(e.target.value as CoverageGapClosingReason)}
      >
        {COVERAGE_GAP_CLOSING_REASONS.map((value) => (
          <MenuItem key={value} value={value}>
            {COVERAGE_GAP_REASON_LABELS[value]}
          </MenuItem>
        ))}
      </TextField>
      {reason === "filled" ? (
        <Note>
          Each claim of the gap is checked again. You see which ones now get an answer, and decide
          whether the gap stays closed.
        </Note>
      ) : null}
      <TextField
        label="Note"
        size="small"
        fullWidth
        multiline
        minRows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        slotProps={{ htmlInput: { maxLength: COVERAGE_GAP_NOTE_MAX_LENGTH } }}
        placeholder="Why it is closed, what filled it…"
      />
    </FormDialog>
  );
}
